import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, RefreshCw, LogOut, Mail, User, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "nrn.admin.key";

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminLeads() {
  const [adminKey, setAdminKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, entries: [] });

  useEffect(() => {
    document.title = "Admin · Nessa Rescue Node";
    const stored = localStorage.getItem(KEY_STORAGE);
    if (stored) {
      verify(stored, /* silent */ true);
    }
  }, []);

  const verify = async (key, silent = false) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, null, {
        headers: { "X-Admin-Key": key },
      });
      localStorage.setItem(KEY_STORAGE, key);
      setAdminKey(key);
      setAuthed(true);
      await loadLeads(key);
      if (!silent) toast.success("Command access granted");
    } catch (err) {
      localStorage.removeItem(KEY_STORAGE);
      setAuthed(false);
      if (!silent) toast.error("Access denied", { description: "Invalid command key" });
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async (key) => {
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { "X-Admin-Key": key || adminKey },
      });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load roster");
    }
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
    setAuthed(false);
    setData({ total: 0, entries: [] });
  };

  const exportCsv = () => {
    const rows = [
      ["timestamp", "email", "callsign", "source"],
      ...data.entries.map((e) => [
        e.timestamp || "",
        e.email || "",
        e.callsign || "",
        e.source || "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `nessa-armada-roster-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!authed) {
    return (
      <div className="min-h-screen obsidian-grid grain grid place-items-center px-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify(adminKey);
          }}
          className="w-full max-w-sm rounded-2xl glass p-8 relative"
          data-testid="admin-login-form"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-3.5 h-3.5 text-gold" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/90">
              Command Authority · 25D
            </span>
          </div>
          <h1 className="font-heading text-2xl text-white">Admiral&apos;s Console</h1>
          <p className="mt-2 text-sm text-white/55">
            Provide the command key to view the Armada roster.
          </p>

          <div className="mt-6">
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/45 block mb-1.5">
              Command Key
            </label>
            <Input
              type="password"
              required
              autoFocus
              value={adminKey}
              data-testid="admin-key-input"
              onChange={(e) => setAdminKey(e.target.value)}
              className="bg-black/60 border-white/10 text-white font-mono focus-visible:border-gold/50 focus-visible:ring-gold/20 h-11"
              placeholder="••••••••"
            />
          </div>

          <Button
            type="submit"
            disabled={loading || !adminKey}
            data-testid="admin-login-submit"
            className="mt-5 w-full h-11 bg-gold hover:bg-gold-soft text-obsidian font-mono text-[11px] tracking-[0.22em] uppercase rounded-full"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" /> Enter Console
              </>
            )}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen obsidian-grid grain text-foreground" data-testid="admin-console">
      <header className="border-b border-white/5 glass">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md border border-gold/40 grid place-items-center bg-black">
              <ShieldCheck className="w-4 h-4 text-gold" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[15px] tracking-[0.18em] text-white">
                ADMIRAL&apos;S CONSOLE
              </div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-gold/80 uppercase">
                Armada Roster · Live
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => loadLeads()}
              data-testid="admin-refresh-btn"
              className="bg-transparent border-white/10 text-white/70 hover:text-white hover:bg-white/5 font-mono text-[10px] tracking-[0.22em] uppercase h-9"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              data-testid="admin-export-csv-btn"
              className="bg-transparent border-gold/30 text-gold hover:bg-gold/10 font-mono text-[10px] tracking-[0.22em] uppercase h-9"
            >
              Export CSV
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-white/55 hover:text-white font-mono text-[10px] tracking-[0.22em] uppercase h-9"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-7">
          <Stat label="Total Operators" value={data.total} testid="stat-total" />
          <Stat label="Manifesto Pulls" value={data.entries.filter((e) => e.source === "manifesto-vol-0").length} testid="stat-pulls" />
          <Stat
            label="Latest"
            value={
              data.entries[0]?.timestamp
                ? fmtTime(data.entries[0].timestamp).split(",")[0]
                : "—"
            }
            testid="stat-latest"
            small
          />
        </div>

        <div className="rounded-2xl glass overflow-hidden" data-testid="leads-table">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60">
              Sovereign Roster
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80">
              · {data.entries.length} records ·
            </div>
          </div>

          {data.entries.length === 0 ? (
            <div className="p-10 text-center text-white/50 font-mono text-sm">
              No operators yet. The Armada is forming.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {data.entries.map((e, i) => (
                <div
                  key={e.id || i}
                  data-testid={`lead-row-${i}`}
                  className="grid grid-cols-12 gap-4 px-5 py-3.5 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="col-span-12 sm:col-span-5 flex items-center gap-2 min-w-0">
                    <Mail className="w-3.5 h-3.5 text-gold/80 shrink-0" />
                    <span className="font-mono text-sm text-white truncate">
                      {e.email}
                    </span>
                  </div>
                  <div className="col-span-6 sm:col-span-3 flex items-center gap-2 min-w-0">
                    <User className="w-3.5 h-3.5 text-violet/80 shrink-0" />
                    <span className="text-white/65 truncate">
                      {e.callsign || <span className="text-white/30">—</span>}
                    </span>
                  </div>
                  <div className="col-span-6 sm:col-span-2 font-mono text-[10px] tracking-[0.18em] uppercase text-white/45 flex items-center">
                    {e.source}
                  </div>
                  <div className="col-span-12 sm:col-span-2 flex items-center gap-2 sm:justify-end">
                    <Clock className="w-3 h-3 text-white/35 shrink-0" />
                    <span className="font-mono text-[11px] text-white/55">
                      {fmtTime(e.timestamp)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, testid, small }) {
  return (
    <div className="rounded-xl glass p-5" data-testid={testid}>
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/45">
        {label}
      </div>
      <div
        className={`mt-1.5 font-mono ${
          small ? "text-lg" : "text-3xl"
        } gold-text tracking-tight`}
      >
        {value}
      </div>
    </div>
  );
}
